'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SessionProvider, useSession } from 'next-auth/react';
import { ThemeProvider } from 'next-themes';
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Toaster } from '@/components/ui/toaster';
import { LocaleSync } from '@/components/providers/locale-sync';

interface ProvidersProps {
  children: ReactNode;
}

// H5: TokenSync and the apiClient.setToken() pattern have been removed.
// The Django JWT is now handled entirely server-side by /api/proxy.
// This context signals when the NextAuth session has finished loading so
// TanStack Query hooks don't fire before auth state is known.
const SessionReadyContext = createContext(false);

/** Returns true once the NextAuth session has resolved (authenticated or not). */
export function useIsSessionReady(): boolean {
  return useContext(SessionReadyContext);
}

/** @deprecated Use useIsSessionReady() — renamed as part of H5 proxy migration. */
export const useIsTokenReady = useIsSessionReady;

function SessionReadySync({
  onReady,
  queryClient,
}: {
  onReady: (ready: boolean) => void;
  queryClient: QueryClient;
}) {
  const { data: session, status } = useSession();
  const userId = session?.user?.id;
  const prevUserIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (status !== 'loading') onReady(true);
  }, [status, onReady]);

  // Clear ALL cached query data when the logged-in user changes.
  // Without this, a stale ['currentUser'] cache from a previous session
  // (or a different user on the same browser) is served for up to 15 minutes,
  // causing the tenant switcher to show no organisation.
  useEffect(() => {
    if (status === 'authenticated' && userId) {
      if (prevUserIdRef.current !== userId) {
        // User changed (or first login) — wipe all cached tenant-scoped data
        queryClient.clear();
      }
      prevUserIdRef.current = userId;
    }
    if (status === 'unauthenticated') {
      prevUserIdRef.current = undefined;
    }
  }, [userId, status, queryClient]);

  return null;
}

export function Providers({ children }: ProvidersProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000, // 1 minute
            retry: 1,
            refetchOnWindowFocus: false,
          },
        },
      })
  );
  const [sessionReady, setSessionReady] = useState(false);

  return (
    <SessionProvider refetchInterval={2 * 60} refetchOnWindowFocus={true}>
      <SessionReadySync onReady={setSessionReady} queryClient={queryClient} />
      <QueryClientProvider client={queryClient}>
        <SessionReadyContext.Provider value={sessionReady}>
          <ThemeProvider
            attribute="class"
            defaultTheme="dark"
            enableSystem
            disableTransitionOnChange
          >
            <LocaleSync />
            {children}
            <Toaster />
          </ThemeProvider>
          {/* FE-L5: ReactQueryDevtools removed — incompatible with turbopack (Next 14.2).
             Use the standalone browser extension or `dev:webpack` script if devtools needed.
             Re-evaluate after upgrading to Next.js 15+ where turbopack is stable. */}
        </SessionReadyContext.Provider>
      </QueryClientProvider>
    </SessionProvider>
  );
}
